/**
 * Single source of truth for competitive session match format (doubles vs singles).
 * DB column: sessions.competitive_match_format, club_session_setup.competitive_match_format
 */

export const MATCH_FORMAT_DOUBLES = 'doubles';
export const MATCH_FORMAT_SINGLES = 'singles';

/** @param {object|null|undefined} session */
export function getMatchFormat(session) {
  if (!session) return MATCH_FORMAT_DOUBLES;
  const v = session.competitive_match_format;
  return v === MATCH_FORMAT_SINGLES ? MATCH_FORMAT_SINGLES : MATCH_FORMAT_DOUBLES;
}

/** @param {string} format */
export function isSinglesFormat(format) {
  return format === MATCH_FORMAT_SINGLES;
}

/** @param {string} format */
export function getPlayersPerSide(format) {
  return isSinglesFormat(format) ? 1 : 2;
}

/** @param {string} format */
export function getPlayersPerMatch(format) {
  return getPlayersPerSide(format) * 2;
}

/**
 * Singles semantics for game_type_restriction (same enum as doubles; different allowed pair types).
 * Returns flags used by singles matchmaking.
 * @param {string} restriction - sessions.game_type_restriction enum value
 * @param {string} format
 * @returns {{ allowLevelsSingles: boolean, allowUnbalancedSingles: boolean }}
 */
export function getSinglesPairingPolicy(restriction, format) {
  if (!isSinglesFormat(format)) {
    return { allowLevelsSingles: true, allowUnbalancedSingles: true };
  }
  switch (restriction) {
    case 'levels_only':
      return { allowLevelsSingles: true, allowUnbalancedSingles: false };
    case 'mixed_only':
      // Legacy enum: was "cross-gender 1v1 only"; product now treats as all types for singles
      return { allowLevelsSingles: true, allowUnbalancedSingles: true };
    case 'balanced_only':
    case 'balanced_and_mens_vs_mixed':
      // Same-gender competitive singles + optional cross-gender for the "and mens vs mixed" flavor
      return {
        allowLevelsSingles: true,
        allowUnbalancedSingles: restriction === 'balanced_and_mens_vs_mixed',
      };
    case 'all_types':
    default:
      return { allowLevelsSingles: true, allowUnbalancedSingles: true };
  }
}

/**
 * Map stored DB enum to the singles dropdown value (see singlesGameTypeRestrictionOptions).
 * Keeps older templates/sessions working: balanced_* / mixed_only map to the closest offered option.
 */
export function singlesGameTypeRestrictionSelectValue(restriction) {
  const r = restriction || 'all_types';
  if (r === 'levels_only' || r === 'all_types') return r;
  if (r === 'balanced_only') return 'levels_only';
  if (r === 'balanced_and_mens_vs_mixed' || r === 'mixed_only') return 'all_types';
  return 'all_types';
}

/**
 * Options for Select when competitive session is in singles mode (reuse enum values).
 */
export const singlesGameTypeRestrictionOptions = [
  { value: 'all_types', label: 'All types', shortLabel: 'All types' },
  { value: 'levels_only', label: 'Levels only', shortLabel: 'Levels only' },
];

/**
 * Doubles game type restriction options for selects and session config.
 * `shortLabel` is used in compact in-session toolbar selects; `label` is the form-facing name.
 */
export const doublesGameTypeRestrictionOptions = [
  {
    value: 'balanced_only',
    label: 'Balanced Games - (Mens/Womens/Mixed)',
    shortLabel: 'Balanced games',
  },
  {
    value: 'balanced_and_mens_vs_mixed',
    label: 'Balanced + mens vs mixed',
    shortLabel: 'Bal. + men vs mixed',
  },
  {
    value: 'all_types',
    label: 'All game types',
    shortLabel: 'All types',
  },
  {
    value: 'levels_only',
    label: 'Levels only',
    shortLabel: 'Levels only',
  },
  {
    value: 'mixed_only',
    label: 'Mixed only',
    shortLabel: 'Mixed only',
  },
];
